<script setup>
// SiteUp: hub dos 6 mundos. Única porta de navegação. Linguagem visual do
// design system estilo X (skill siteup-x-design-system).
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useOnboardingSurface } from 'dashboard/composables/useOnboardingSurface';
import { getModifierKey } from 'dashboard/composables/utils/useKbd';
import { useHubWorlds } from './useHubWorlds';
import { useHubLiveMeta } from './useHubLiveMeta';
import WorkspaceV2Hub from 'dashboard/components-next/workspaceV2/WorkspaceV2Hub.vue';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { Gradient } from './gradient';
import Logo from 'next/icon/Logo.vue';
import AvisoUpgrade from 'dashboard/components-next/Planos/AvisoUpgrade.vue';
import BrasaMascot from 'dashboard/components-next/onboarding/BrasaMascot.vue';
// O seletor de empresa saiu da navegacao no #483, quando a sidebar de 21 itens
// foi apagada: ele sobreviveu SO dentro do dock do Modo Foco, que e condicional
// (some com a lista visivel e em tela pequena). Quem opera mais de uma conta
// ficou sem caminho nenhum para trocar a partir do Hub, que e a porta unica.
import SidebarAccountSwitcher from 'dashboard/components-next/sidebar/SidebarAccountSwitcher.vue';
import HubPersonalBar from './HubPersonalBar.vue';
import { waitingDurationFromMinutes } from 'dashboard/helper/waitingLabel';

const { isWorkspaceV2 } = useWorkspaceV2();
const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const { visibleWorlds } = useHubWorlds();
const {
  mineCount,
  unassignedCount,
  maxWaitMinutes,
  liveMetaByWorld,
  liveSignalByWorld,
  hasUnreadRelease,
} = useHubLiveMeta(visibleWorlds);

// "Com você" e "sem ninguém" lado a lado: o número grande é a fila de quem
// está logado, e a da equipe sem responsável fica visível junto dele.
const unassignedLabel = computed(() =>
  t('HUB.HOME.QUEUE_UNASSIGNED', unassignedCount.value || 0, {
    count: unassignedCount.value || 0,
  })
);

// A maior espera vinha em minutos crus: 40 dias apareciam como "57829 min".
// A escala (min → h → dias → semanas) mora em helper/waitingLabel.js.
const maxWaitLabel = computed(() =>
  waitingDurationFromMinutes(maxWaitMinutes.value, t)
);

const currentUser = useMapGetter('getCurrentUser');
const gradientCanvas = ref(null);
const GRADIENT_ID = 'hub-attendance-gradient';
const useAnimatedGradient = ref(false);
let gradientInstance = null;
let gradientStartToken = 0;

const firstName = computed(() => {
  const name =
    currentUser.value?.available_name || currentUser.value?.name || '';
  return name.split(' ')[0];
});

const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_MORNING');
  if (hour < 18) return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_AFTERNOON');
  return t('SIDEBAR.FOCUS_MODE.HUB_GOOD_EVENING');
});

const attendanceWorld = computed(
  () => visibleWorlds.value.find(world => world.id === 'attendance') || null
);

const queueRoute = computed(
  () =>
    attendanceWorld.value?.primaryDestination?.to || accountScopedRoute('home')
);

const goTo = to => router.push(to);

const destinationRoute = destination =>
  accountScopedRoute(
    destination.routeName,
    destination.params || {},
    destination.query || {}
  );

const openQueue = () => goTo(queueRoute.value);

const searchShortcut = computed(() => `${getModifierKey()}K`);

const openCommandBar = (query = '') => {
  const ninja = document.querySelector('ninja-keys');
  if (!ninja || typeof ninja.open !== 'function') return;
  ninja.open();
  if (!query) return;
  nextTick(() => {
    const input =
      ninja.shadowRoot?.querySelector('input') || ninja.querySelector('input');
    if (!input) return;
    input.value = query;
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });
};

const onSearchKeydown = event => {
  if (event.metaKey || event.ctrlKey || event.altKey) return;
  // Teclado virtual (Android) e IME mandam 'Unidentified'/'Process': o
  // caractere só chega no input, e o onSearchInput leva ele junto.
  if (event.isComposing || ['Unidentified', 'Process'].includes(event.key)) {
    return;
  }
  if (
    [
      'Tab',
      'Shift',
      'Escape',
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
      'Backspace',
      'Delete',
    ].includes(event.key)
  ) {
    return;
  }
  event.preventDefault();
  const seed = event.key.length === 1 ? event.key : '';
  openCommandBar(seed);
};

// O campo do Hub é só a porta da busca: nada lê o valor dele. Colar (Ctrl+V
// ou menu do botão direito) e o texto que entrar por fora do keydown vão para
// a barra de comandos, e o campo fica vazio.
const onSearchPaste = event => {
  event.preventDefault();
  openCommandBar(event.clipboardData?.getData('text') || '');
};

const onSearchInput = event => {
  const text = event.target.value;
  event.target.value = '';
  if (text) openCommandBar(text);
};

// Quatro portas: Atender grande à esquerda, Vender ao lado, Ações de
// Marketing e Automações na fileira de baixo.
const worldCardClass = world => {
  if (world.id === 'attendance') {
    return 'hub-card hub-card--primary col-span-1 min-h-[250px] md:col-span-12 lg:col-span-8 lg:row-span-2';
  }
  if (world.id === 'sales') {
    return 'hub-card col-span-1 min-h-[188px] md:col-span-12 lg:col-span-4 lg:row-span-2 lg:min-h-[204px]';
  }
  return 'hub-card col-span-1 min-h-[188px] md:col-span-6 lg:col-span-6 lg:min-h-[204px]';
};

const showAttendanceGradient = computed(() => Boolean(attendanceWorld.value));

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const canUseWebGL = () => {
  try {
    const probe = document.createElement('canvas');
    return Boolean(
      probe.getContext('webgl') || probe.getContext('experimental-webgl')
    );
  } catch {
    return false;
  }
};

const destroyGradient = () => {
  if (!gradientInstance) return;
  try {
    gradientInstance.pause();
    gradientInstance.disconnect();
    gradientInstance.minigl?.gl
      ?.getExtension('WEBGL_lose_context')
      ?.loseContext();
  } catch {
    // Canvas already gone.
  }
  gradientInstance = null;
};

// Movimento bem lento de propósito: o cartão precisa parecer vivo sem puxar o
// olho de quem está lendo a fila, então fica abaixo até do preset do Stripe
// (1.4e-5). Ajustamos o ruído assim que o mesh existe, e o init do Gradient é
// assíncrono, daí o poll curto.
const GRADIENT_NOISE_SPEED = 1e-5;
const energizeGradient = (gradient, token) => {
  let tentativas = 0;
  const acelerar = () => {
    if (token !== gradientStartToken || gradientInstance !== gradient) return;
    const uniforms = gradient.mesh?.material?.uniforms;
    if (!uniforms) {
      tentativas += 1;
      if (tentativas < 120) requestAnimationFrame(acelerar);
      return;
    }
    uniforms.u_global.value.noiseSpeed.value = GRADIENT_NOISE_SPEED;
    uniforms.u_waveLayers.value.forEach(layer => {
      layer.value.noiseCeil.value = Math.min(
        0.86,
        layer.value.noiseCeil.value + 0.05
      );
    });
  };
  requestAnimationFrame(acelerar);
};

const startGradient = async () => {
  const token = gradientStartToken + 1;
  gradientStartToken = token;
  destroyGradient();
  if (!useAnimatedGradient.value) return;
  await nextTick();
  if (token !== gradientStartToken) return;
  // o ref pode não estar preenchido no primeiro tick pós-flag
  let canvas = gradientCanvas.value || document.getElementById(GRADIENT_ID);
  if (!canvas) {
    await nextTick();
    canvas = gradientCanvas.value || document.getElementById(GRADIENT_ID);
  }
  if (!canvas || token !== gradientStartToken) return;
  try {
    const gradient = new Gradient();
    gradient.initGradient(`#${GRADIENT_ID}`);
    if (!gradient.minigl?.gl) {
      destroyGradient();
      useAnimatedGradient.value = false;
      return;
    }
    gradientInstance = gradient;
    energizeGradient(gradient, token);
  } catch (error) {
    destroyGradient();
    useAnimatedGradient.value = false;
  }
};

// Cada card abre a tela de início da área (hub/:worldId): opções e números.
const openWorld = world =>
  world.id === 'ai_center'
    ? router.push(
        world.primaryDestination?.to || accountScopedRoute('ai_center')
      )
    : router.push(accountScopedRoute('hub_world_home', { worldId: world.id }));

// Medir e Configurar saíram dos cards: relatórios vivem dentro das áreas e
// as configurações ficam aqui, num acesso discreto para quem administra.
// Novidades e Central de ajuda moram no mundo Configurar, que não tem cartão:
// sem estes dois links ninguém chegava nelas pelo Hub, e o ponto de versão
// nova era calculado para ninguém ver. Valem para todos os papéis.
const { isAdmin } = useAdmin();
// Manual e tour só existem no desktop web: fora dele (celular e PWA instalado)
// a Central de ajuda só mostraria o aviso de indisponível. Mesma regra do
// destino do mapa em useHubWorlds.
const { enabled: onboardingSurface } = useOnboardingSurface();
const footerLinks = computed(() => [
  ...(isAdmin.value
    ? [
        {
          id: 'reports',
          labelKey: 'HUB.FOOTER_REPORTS',
          icon: 'i-ri-bar-chart-line',
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          id: 'settings',
          labelKey: 'HUB.FOOTER_SETTINGS',
          icon: 'i-ri-settings-3-line',
          to: accountScopedRoute('settings_menu'),
        },
      ]
    : []),
  {
    id: 'release_notes',
    labelKey: 'HUB.DESTINATIONS.RELEASE_NOTES',
    icon: 'i-ri-sparkling-line',
    to: accountScopedRoute('release_notes'),
    unread: Boolean(hasUnreadRelease?.value),
  },
  ...(onboardingSurface.value
    ? [
        {
          id: 'help_guides',
          labelKey: 'HUB.DESTINATIONS.HELP_GUIDES',
          icon: 'i-ri-lifebuoy-line',
          to: accountScopedRoute('help_guides'),
        },
      ]
    : []),
]);

const worldLiveMeta = world => liveMetaByWorld.value[world.id] || '';

const worldLiveSignal = world => Boolean(liveSignalByWorld.value[world.id]);

watch(showAttendanceGradient, visible => {
  if (visible) startGradient();
  else destroyGradient();
});

onMounted(async () => {
  useAnimatedGradient.value = !prefersReducedMotion() && canUseWebGL();
  // O <canvas> é v-if da flag acima: ele só entra no DOM no render seguinte.
  // Sem esperar, initGradient não acha o elemento e a animação nunca começa.
  await nextTick();
  if (showAttendanceGradient.value) startGradient();
});

onBeforeUnmount(() => {
  destroyGradient();
});
</script>

<template>
  <WorkspaceV2Hub
    v-if="isWorkspaceV2"
    :visible-worlds="visibleWorlds"
    :live-meta-by-world="liveMetaByWorld"
  />
  <div
    v-else
    class="hub flex flex-1 min-h-0 w-full flex-col items-center overflow-y-auto bg-n-background px-5 py-[clamp(38px,6vw,76px)] sm:px-6"
  >
    <!-- Empresa atual, e a troca quando ha mais de uma. Faixa propria em vez
           de espremer a saudacao, e sem esconder no mobile: ali tambem nao
           existe outro caminho (o MobileBottomNav nao tem seletor). O proprio
           componente decide o comportamento: com uma conta so ele fica inerte,
           sem seta. -->
    <div
      class="hub-account mb-4 flex w-full max-w-[1180px] items-center justify-between gap-3"
    >
      <div
        class="flex min-w-0 max-w-[260px] items-center rounded-xl border border-n-weak bg-n-solid-1 py-1.5"
        data-testid="hub-account-switcher"
      >
        <SidebarAccountSwitcher placement="bottom" />
      </div>
      <!-- Só no celular: ali o dock não existe, e sem isto não havia como
           trocar o status, ver notificações ou sair. -->
      <HubPersonalBar class="md:hidden" />
    </div>

    <div
      class="hub-greet mb-7 grid w-full max-w-[1180px] items-end gap-8 lg:grid-cols-[minmax(0,1fr)_auto]"
    >
      <div class="flex min-w-0 items-center gap-3">
        <div class="grid size-9 shrink-0 place-content-center">
          <Logo class="size-5" />
        </div>
        <h1
          class="m-0 max-w-[760px] text-[clamp(26px,4vw,42px)] font-medium leading-[1.06] tracking-tight text-n-slate-12"
        >
          {{ greeting }}<template v-if="firstName">, {{ firstName }}</template>
        </h1>
      </div>

      <!-- Sem aria-label aqui: div sem papel não é anunciado, e o botão ao
           lado já leva o rótulo "Abrir fila". -->
      <div
        class="hub-queue-quick flex min-h-11 w-full min-w-0 items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 py-2.5 pl-4 pr-2.5 lg:w-auto lg:min-w-[260px]"
        data-testid="hub-queue-quick"
      >
        <span
          class="size-2 shrink-0 rounded-full bg-n-teal-9"
          aria-hidden="true"
        />
        <div class="min-w-0 flex-1">
          <strong class="block text-[14px] tabular-nums text-n-slate-12">
            {{ t('HUB.QUEUE_WAITING', { count: mineCount }) }}
            <span
              class="font-medium text-n-slate-11"
              data-testid="hub-queue-unassigned"
            >
              · {{ unassignedLabel }}
            </span>
          </strong>
          <span
            v-if="maxWaitMinutes !== null"
            class="mt-0.5 block text-[12px] tabular-nums text-n-slate-11"
          >
            {{ t('HUB.MAX_WAIT', { wait: maxWaitLabel }) }}
          </span>
        </div>
        <button
          v-tooltip="t('HUB.OPEN_QUEUE_ARIA')"
          type="button"
          class="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-n-slate-12 text-n-solid-1 transition-transform duration-150 hover:bg-n-slate-11"
          :aria-label="t('HUB.OPEN_QUEUE_ARIA')"
          @click="openQueue"
        >
          <span class="i-ri-arrow-right-line size-5" />
        </button>
      </div>
    </div>

    <!-- O clique no label chega ao input pela ativação do label: um handler
         só, no input, para a barra não abrir duas vezes. -->
    <label
      data-testid="hub-search"
      class="hub-search mb-7 flex min-h-11 w-full max-w-[1180px] cursor-text items-center gap-3 rounded-2xl border border-n-weak bg-n-solid-1 px-4 focus-within:border-n-brand"
    >
      <span
        class="i-ri-search-line size-4 shrink-0 text-n-slate-10"
        aria-hidden="true"
      />
      <input
        type="search"
        class="reset-base !m-0 min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-sm font-medium !text-n-slate-12 !shadow-none outline-none focus:!ring-0 placeholder:!text-n-slate-10"
        :placeholder="t('HUB.SEARCH_PLACEHOLDER')"
        :aria-label="t('HUB.SEARCH_ARIA')"
        autocomplete="off"
        @click="openCommandBar()"
        @keydown="onSearchKeydown"
        @paste="onSearchPaste"
        @input="onSearchInput"
      />
      <kbd
        class="inline-flex min-h-6 min-w-[26px] items-center justify-center rounded-md border border-n-weak bg-n-background px-1.5 text-[12px] font-bold tabular-nums text-n-slate-11"
      >
        {{ searchShortcut }}
      </kbd>
    </label>

    <div
      class="hub-grid grid w-full max-w-[1180px] grid-cols-1 gap-3.5 md:grid-cols-12"
    >
      <template v-for="world in visibleWorlds" :key="world.id">
        <article
          :class="worldCardClass(world)"
          :data-testid="
            world.id === 'attendance' ? 'hub-world-attendance' : undefined
          "
        >
          <template v-if="world.id === 'attendance'">
            <canvas
              v-if="useAnimatedGradient"
              :id="GRADIENT_ID"
              ref="gradientCanvas"
              class="hub-gradient-canvas"
              aria-hidden="true"
            />
            <div v-else class="hub-gradient-static" aria-hidden="true" />
            <div class="hub-gradient-veil" aria-hidden="true" />
          </template>

          <div
            class="hub-card-body"
            :class="{
              'hub-card-body--shortcuts':
                world.id === 'ai_center' && !world.featured,
            }"
          >
            <div>
              <div class="flex items-start justify-between gap-4">
                <span class="hub-ico">
                  <BrasaMascot
                    v-if="world.id === 'automation'"
                    state="idle"
                    :size="20"
                    :animated="false"
                  />
                  <span v-else class="size-5" :class="[world.icon]" />
                </span>
                <span class="hub-count">
                  {{
                    t(
                      'HUB.OPTION_COUNT',
                      world.optionCount ?? world.destinationCount,
                      { count: world.optionCount ?? world.destinationCount }
                    )
                  }}
                </span>
              </div>
              <div class="hub-copy">
                <h2 class="hub-name">{{ t(world.labelKey) }}</h2>
                <p class="hub-meta">{{ t(world.descriptionKey) }}</p>
              </div>
            </div>

            <div class="hub-card-foot">
              <template v-if="world.featured">
                <div>
                  <div class="hub-live-queue">
                    <strong>{{ mineCount }}</strong>
                    <span>{{ t('HUB.WAITING_LABEL') }}</span>
                  </div>
                  <span
                    class="hub-wait-note !flex"
                    data-testid="hub-card-unassigned"
                  >
                    {{ unassignedLabel }}
                  </span>
                  <span v-if="maxWaitMinutes !== null" class="hub-wait-note">
                    <span class="hub-wait-dot" aria-hidden="true" />
                    {{ t('HUB.MAX_WAIT_SHORT', { wait: maxWaitLabel }) }}
                  </span>
                </div>
                <div class="flex flex-wrap justify-end gap-2">
                  <button
                    v-if="world.primaryDestination"
                    type="button"
                    class="hub-pill hub-pill--solid min-h-11"
                    @click="goTo(world.primaryDestination.to)"
                  >
                    {{ t(world.primaryAction.labelKey) }}
                    <span class="i-ri-arrow-right-line size-4" />
                  </button>
                  <button
                    type="button"
                    class="hub-pill hub-pill--ghost min-h-11"
                    @click="openWorld(world)"
                  >
                    {{ t('HUB.VIEW_DESTINATIONS') }}
                  </button>
                </div>
              </template>
              <template v-else>
                <div
                  v-if="world.id === 'ai_center'"
                  class="flex min-w-0 flex-col gap-3"
                >
                  <!-- Total de pendentes da Central, no padrão dos outros
                       cartões: número vivo e ponto quando há o que revisar. -->
                  <span
                    v-if="worldLiveMeta(world)"
                    class="hub-mini"
                    data-testid="hub-ai-center-pending"
                  >
                    <span
                      v-if="worldLiveSignal(world)"
                      class="hub-wait-dot"
                      aria-hidden="true"
                    />
                    {{ worldLiveMeta(world) }}
                  </span>
                  <div
                    class="hub-card-shortcuts flex flex-wrap gap-2"
                    data-testid="hub-ai-center-shortcuts"
                  >
                    <button
                      v-for="destination in world.destinations.slice(1)"
                      :key="destination.id"
                      type="button"
                      class="hub-pill hub-pill--ghost min-h-9 px-3 text-xs"
                      @click.stop="goTo(destinationRoute(destination))"
                    >
                      {{ t(destination.labelKey) }}
                    </button>
                  </div>
                </div>
                <template v-else>
                  <span v-if="worldLiveMeta(world)" class="hub-mini">
                    <span
                      v-if="worldLiveSignal(world)"
                      class="hub-wait-dot"
                      aria-hidden="true"
                    />
                    {{ worldLiveMeta(world) }}
                  </span>
                  <span v-else class="hub-mini" />
                  <span class="hub-route">
                    {{ t('HUB.OPEN') }}
                    <span class="i-ri-arrow-right-line size-4" />
                  </span>
                </template>
              </template>
            </div>
          </div>

          <button
            v-if="!world.featured"
            type="button"
            class="absolute inset-0 z-10 rounded-2xl"
            :aria-label="t(world.labelKey)"
            @click="openWorld(world)"
          />
        </article>
      </template>
    </div>

    <!-- Depois dos cartões de propósito: o hub existe para levar a pessoa ao
           trabalho dela, e aviso comercial acima deles empurra a decisão para
           baixo. Aqui ele é lido por escolha, e o componente já se esconde
           sozinho para quem não pode contratar. -->
    <AvisoUpgrade class="mt-6 w-full max-w-[1180px]" />

    <nav
      class="hub-links mt-6 flex w-full max-w-[1180px] flex-wrap items-center gap-2"
      :aria-label="t('HUB.FOOTER_LABEL')"
      data-testid="hub-footer-links"
    >
      <button
        v-for="link in footerLinks"
        :key="link.id"
        type="button"
        class="hub-link min-h-11 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
        :data-testid="`hub-footer-${link.id}`"
        @click="goTo(link.to)"
      >
        <span class="size-4" :class="[link.icon]" aria-hidden="true" />
        {{ t(link.labelKey) }}
        <template v-if="link.unread">
          <span
            class="size-2 rounded-full bg-n-teal-9"
            aria-hidden="true"
            data-testid="hub-footer-unread"
          />
          <span class="sr-only">{{ t('HUB.FOOTER_UNREAD') }}</span>
        </template>
      </button>
    </nav>

    <p
      class="hub-foot mt-6 m-0 w-full max-w-[1180px] text-[13px] text-n-slate-10"
    >
      {{ t('HUB.FOOTNOTE', { shortcut: searchShortcut }) }}
    </p>
  </div>
</template>

<style scoped>
.hub-card {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: stretch;
  overflow: hidden;
  padding: 22px;
  text-align: start;
  background: rgb(var(--solid-1));
  border: 1px solid rgb(var(--border-weak));
  @apply rounded-[var(--workspace-radius,16px)];
  cursor: pointer;
  font-family: inherit;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    border-color var(--motion-duration-hover) var(--motion-ease-out),
    transform var(--motion-duration-press) var(--motion-ease-out);
}
.hub-card:hover {
  background: rgb(var(--solid-2));
}
.hub-card:active {
  transform: scale(0.98);
}
.hub-card:hover .hub-route :last-child {
  transform: translateX(3px);
}
.hub-card--primary {
  background: #1a0f3d;
  border-color: #2e1b6b;
  /* o cartão tem gradiente escuro: tudo aqui dentro herda texto claro */
  color: #f4f2ff;
  cursor: default;
}
.hub-card--primary:hover {
  background: #1a0f3d;
}
.hub-card--primary:active {
  transform: none;
}
.hub-card--primary .hub-name {
  /* texto claro: o cartão tem gradiente escuro por baixo, não mais fundo claro */
  color: #f4f2ff;
}
.hub-card--primary .hub-meta,
.hub-card--primary .hub-count {
  color: rgb(244 242 255 / 0.78);
}
.hub-card--primary .hub-count {
  border-color: rgb(244 242 255 / 0.28);
}
.hub-card--primary .hub-ico {
  /* sobre o gradiente: círculo translúcido com ícone claro, não bloco branco */
  background: rgb(244 242 255 / 0.14);
  color: #f4f2ff;
}
/* Cor por token: o #0bd8b6 chumbado era o valor DARK do --teal-11 e dava
   contraste 1.57:1 no tema claro (o card fica branco). */
.hub-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.9rem;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 9999px;
  background: rgb(var(--solid-1));
  color: rgb(var(--slate-11));
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    color var(--motion-duration-hover) var(--motion-ease-out);
}
.hub-link:hover {
  background: rgb(var(--solid-2));
  color: rgb(var(--slate-12));
}
.hub-card-body {
  position: relative;
  z-index: 2;
  display: flex;
  min-height: 100%;
  flex-direction: column;
  justify-content: space-between;
}
/* Cartão com atalhos (Central da IA): o botão que cobre o cartão (z 10)
   ficava por cima das abas e o clique abria a Central na aba padrão. O corpo
   sobe acima dele sem receber clique; só os atalhos voltam a receber. */
.hub-card-body--shortcuts {
  z-index: 11;
  pointer-events: none;
}
.hub-card-shortcuts {
  pointer-events: auto;
}
.hub-gradient-canvas,
.hub-gradient-static,
.hub-gradient-veil {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.hub-gradient-canvas {
  z-index: 0;
  display: block;
  /* A cor 1 é a base do mesh: com ela escura o cartão fica preto sempre que
     o ruído fecha as camadas. Base já roxa mantém o cartão vivo em qualquer
     fase da animação. */
  --gradient-color-1: #4b2fa8;
  --gradient-color-2: #6d4bd8;
  --gradient-color-3: #8b6bff;
  --gradient-color-4: #a78bfa;
}
.hub-gradient-static {
  z-index: 0;
  /* Gradiente vivo em CSS: 3 manchas roxas que orbitam sobre uma base que
     desliza. Faz o trabalho do WebGL do Stripe sem 881 linhas nem ciclo de
     vida de canvas — e anima em qualquer navegador. */
  background: radial-gradient(58% 78% at 18% 24%, #8b6bff 0%, transparent 60%),
    radial-gradient(50% 68% at 84% 74%, #a78bfa 0%, transparent 56%),
    radial-gradient(66% 84% at 60% 8%, #4b2fa8 0%, transparent 62%),
    linear-gradient(135deg, #1a0f3d 0%, #2e1b6b 46%, #4b2fa8 100%);
  background-size:
    190% 190%,
    175% 175%,
    200% 200%,
    220% 220%;
  animation: hub-gradient-drift 13s ease-in-out infinite alternate;
  will-change: background-position;
}
@keyframes hub-gradient-drift {
  0% {
    background-position:
      0% 28%,
      100% 72%,
      42% 0%,
      0% 50%;
  }
  35% {
    background-position:
      64% 6%,
      16% 100%,
      100% 44%,
      62% 18%;
  }
  70% {
    background-position:
      100% 74%,
      58% 18%,
      0% 92%,
      100% 82%;
  }
  100% {
    background-position:
      18% 100%,
      92% 0%,
      72% 26%,
      28% 100%;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hub-gradient-static {
    animation: none;
  }
}
.hub-gradient-veil {
  z-index: 1;
  background: linear-gradient(
    180deg,
    rgb(10 6 24 / 0.06) 0%,
    rgb(10 6 24 / 0.34) 100%
  );
}
.hub-card--dest {
  flex-direction: row;
  align-items: center;
  min-height: 76px;
  justify-content: flex-start;
  gap: 13px;
  padding: 14px 15px;
}
.hub-card--dest .hub-name {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-card--dest .hub-meta {
  margin-top: 3px;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hub-dest-copy {
  /* rótulo e subtítulo são dois spans: sem empilhar aqui, saem colados
     na mesma linha ("Chat ao vivoLive chat, SMS e WhatsApp") */
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}
.hub-dest-end {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgb(var(--slate-11));
}
.hub-dest-badge {
  display: inline-flex;
  min-width: 28px;
  height: 24px;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-radius: 9999px;
  background: rgb(var(--blue-9));
  color: rgb(var(--slate-1));
  font-size: 12px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.hub-ico {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  flex: 0 0 auto;
  border-radius: 9999px;
  background: rgb(var(--solid-3));
  color: rgb(var(--slate-12));
}
.hub-ico--lg {
  width: 3rem;
  height: 3rem;
}
.hub-count {
  display: inline-flex;
  min-height: 28px;
  align-items: center;
  padding: 0 10px;
  border: 1px solid rgb(var(--border-weak));
  border-radius: 9999px;
  color: rgb(var(--slate-11));
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.hub-copy {
  margin-top: 30px;
}
.hub-name {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: rgb(var(--slate-12));
}
.hub-meta {
  max-width: 48ch;
  margin: 7px 0 0;
  font-size: 13px;
  line-height: 1.45;
  color: rgb(var(--slate-11));
}
.hub-card-foot {
  display: flex;
  min-height: 44px;
  align-items: flex-end;
  justify-content: space-between;
  gap: 14px;
  margin-top: 22px;
}
.hub-live-queue {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.hub-live-queue strong {
  font-size: clamp(32px, 4vw, 48px);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: #f4f2ff;
}
.hub-live-queue span {
  color: rgb(244 242 255 / 0.72);
  font-size: 13px;
}
.hub-wait-note {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: 6px;
  color: rgb(244 242 255 / 0.8);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.hub-wait-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: rgb(var(--amber-9));
}
.hub-mini {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: rgb(var(--slate-11));
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.hub-route {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: rgb(var(--slate-11));
  font-size: 13px;
  font-weight: 700;
}
.hub-route :last-child {
  transition: transform 220ms var(--motion-ease-out);
}
.hub-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  padding: 0 17px;
  border: 0;
  border-radius: 9999px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    transform var(--motion-duration-press) var(--motion-ease-out);
}
.hub-pill:active {
  transform: scale(0.96);
}
.hub-pill--solid {
  /* Sobre o gradiente roxo: pílula clara com texto escuro, nunca preta. */
  background: #f4f2ff;
  color: #241553;
}
.hub-pill--solid:hover {
  background: #ffffff;
}
.hub-pill--ghost {
  background: transparent;
  color: #f4f2ff;
  box-shadow: inset 0 0 0 1px rgb(244 242 255 / 0.42);
}
.hub-pill--ghost:hover {
  background: rgb(244 242 255 / 0.12);
}
.hub-pill--dark {
  background: rgb(var(--slate-12));
  color: rgb(var(--background-color));
}
.hub-banner {
  padding: 1rem;
  @apply rounded-[var(--workspace-radius,16px)];
  background: rgb(var(--slate-12));
}
.hub-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.75rem;
  border: 0;
  border-radius: 9999px;
  background: transparent;
  color: rgb(var(--slate-11));
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background var(--motion-duration-hover) var(--motion-ease-out),
    color var(--motion-duration-hover) var(--motion-ease-out);
}
.hub-back:hover {
  background: rgb(var(--solid-2));
  color: rgb(var(--slate-12));
}

@media (max-width: 680px) {
  .hub-copy {
    margin-top: 22px;
  }
  .hub-card-foot {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (prefers-reduced-motion: no-preference) {
  .hub-greet,
  .hub-search,
  .hub-card,
  .hub-foot,
  .hub-world {
    opacity: 0;
    animation: hub-rise var(--motion-duration-enter) var(--motion-ease-out)
      forwards;
  }
  .hub-grid .hub-card:nth-child(1) {
    animation-delay: 90ms;
  }
  .hub-grid .hub-card:nth-child(2) {
    animation-delay: calc(90ms + var(--motion-stagger-step));
  }
  .hub-grid .hub-card:nth-child(3) {
    animation-delay: calc(90ms + var(--motion-stagger-step) * 2);
  }
  .hub-grid .hub-card:nth-child(4) {
    animation-delay: calc(90ms + var(--motion-stagger-step) * 3);
  }
  .hub-grid .hub-card:nth-child(5) {
    animation-delay: calc(90ms + var(--motion-stagger-step) * 4);
  }
  .hub-grid .hub-card:nth-child(6) {
    animation-delay: calc(90ms + var(--motion-stagger-step) * 5);
  }
  .hub-foot {
    animation-delay: 360ms;
  }
}
@keyframes hub-rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
